import { useAuth } from '@/services/auth';
import { useProjectStore } from '@/store/project';
import { useUserStore } from '@/store/user';
import { coreApiBasePath } from '@/utils/apiBasePath';
import { Log } from '@devanjs/log';
import {
  faBox,
  faCommentSmile,
  faGear,
  faHouse,
  faListCheck,
} from '@fortawesome/pro-light-svg-icons';
import type {
  BulkUpdateNotificationsRequest,
  ChangePassword,
  ConfirmPasswordRequest,
  CreateUser,
  ForgotPasswordRequest,
  Login,
  ResetPassword,
  SetPassword,
  Setup2FA,
  UpdateUser,
} from '@teemill/dashboard-user';
import { Configuration, UsersApi } from '@teemill/dashboard-user';
import { useMenu } from '@teemill/modules/menu';
import { isHttpError } from '@teemill/modules/utils';
import retry from 'async-retry';
import Cookie from 'js-cookie';
import { computed, type App } from 'vue';
import type { Router } from 'vue-router';

export const client = new UsersApi(
  new Configuration({
    basePath: coreApiBasePath(),
    baseOptions: {
      headers: {
        'User-Agent': null,
      },
    },
  }),
);

client['axios'].interceptors.request.use((config) => {
  const xsrfCookie = Cookie.get('PODOS-XSRF-TOKEN');

  if (xsrfCookie) {
    config.headers['X-CSRF-TOKEN'] = xsrfCookie;
  }

  return config;
});

export const useUser = () => {
  const store = useUserStore();

  return {
    ready: computed(() => store.ready),
    active: computed(() => store.active),

    get: () => client.getUser(),
    create: (data: CreateUser) =>
      client.createUser({
        createUser: {
          email: data.email,
          password: data.password,
          'g-recaptcha-response': data['g-recaptcha-response'],
        },
      }),
    update: (data: UpdateUser) =>
      client.updateUser({
        updateUser: {
          username: data.username,
          firstName: data.firstName,
          lastName: data.lastName,
          email: data.email,
          avatar: data.avatar,
          preferences: data.preferences,
        },
      }),

    login: (data: Login) =>
      client.login({
        login: {
          email: data.email,
          password: data.password,
          'g-recaptcha-response': data['g-recaptcha-response'],
          one_time_password: data['one_time_password'],
          trust_device: data['trust_device'],
        },
      }),
    logout: () => client.logout(),

    verification: {
      resend: () => client.sendVerificationEmail(),
    },

    notifications: {
      list: (params?: {
        search?: string;
        sortBy?: string[];
        cursor?: string;
        limit?: number;
        group?: string;
      }) => client.getNotifications(params).then(({ data }) => data),
      meta: (params?: { search?: string; breakdown?: boolean }) =>
        client.getNotificationsMeta(params).then(({ data }) => data),
      delete: (id: string) => client.deleteNotification({ notificationId: id }),
      read: (id: string) => client.readNotification({ notificationId: id }),
      readAll: (group?: string) => client.readAllNotifications({ group }),
      bulkRead: (request: BulkUpdateNotificationsRequest) =>
        client.bulkUpdateNotifications({
          bulkUpdateNotificationsRequest: {
            notifications: request.notifications,
          },
        }),
    },

    password: {
      forgot: (data: ForgotPasswordRequest) =>
        client.forgotPassword({
          forgotPasswordRequest: {
            email: data.email,
            'g-recaptcha-response': data['g-recaptcha-response'],
          },
        }),
      reset: (data: ResetPassword) =>
        client.resetPassword({
          resetPassword: {
            email: data.email,
            password: data.password,
            password_confirmation: data.password_confirmation,
          },
        }),
      createSetPasswordToken: () => client.createSetPasswordToken(),
      set: (data: SetPassword) =>
        client.setPassword({
          setPassword: {
            email: data.email,
            password: data.password,
            password_confirmation: data.password_confirmation,
            firstName: data.firstName,
            lastName: data.lastName,
            username: data.username,
            avatar: data.avatar,
            signedEula: data.signedEula,
          },
        }),
      change: (data: ChangePassword) =>
        client.changePassword({
          changePassword: {
            current_password: data.current_password,
            password: data.password,
            password_confirmation: data.password_confirmation,
          },
        }),
      confirm: (data: ConfirmPasswordRequest) =>
        client.confirmPassword({
          confirmPasswordRequest: {
            password: data.password,
          },
        }),
    },

    twoFactor: {
      secret: () => client.get2FASecret().then(({ data }) => data),
      setup: (data: Setup2FA) =>
        client.setup2FA({
          setup2FA: {
            secret: data.secret,
            one_time_password: data.one_time_password,
          },
        }),
    },

    push: {
      subscription: {
        update: (data: PushSubscriptionJSON) =>
          client.subscribeToPushNotifications({
            subscribeToPushNotificationsRequest: {
              subscription: data,
            },
          }),
      },
    },

    api: {
      getToken: () => client.getToken(),
    },
  };
};

export const user = {
  async install(
    app: App,
    {
      router: appRouter,
    }: {
      router: Router;
    },
  ) {
    const auth = useAuth();
    const userStore = useUserStore();

    const projectStore = useProjectStore();
    const menu = useMenu();

    const menuItems = [
      {
        name: 'dashboard',
        title: 'Account',
        to: { name: 'dashboard' },
        icon: faHouse,
        order: 0,
      },
      {
        name: 'user.chat',
        title: 'Chat',
        to: { name: 'chat.dashboard' },
        icon: faCommentSmile,
        order: 1,
      },
      {
        name: 'user.projects',
        title: 'Projects',
        to: { name: 'projects.list' },
        icon: faBox,
        order: 3,
      },
      {
        name: 'user.settings',
        title: 'Settings',
        to: { name: 'account.settings' },
        icon: faGear,
        order: 100,
      },
    ];

    menu.addWhen(() => !!userStore.active && !projectStore.active, ...menuItems);

    const tasksItem = {
      name: 'user.tasks',
      title: 'Tasks',
      to: { name: 'users.tasks' },
      icon: faListCheck,
      order: 4,
    };

    menu.addWhen(
      () =>
        !!userStore.active &&
        !projectStore.active &&
        projectStore.projects.some((p) => p.integrations?.includes('workflow')),
      tasksItem,
    );

    appRouter.beforeEach(async (to, from, next) => {
      if (to.name === 'platform.loading') {
        next();
        return;
      }

      const userRequired =
        [...to.matched.map((m) => m.meta?.requires?.user), to.meta.requires?.user]
          .filter((m) => m !== undefined)
          .at(-1) ?? true;

      Log.tag('user')
        .green('Routing')
        .info(`User ${userRequired ? 'required' : 'not required'} for ${String(to.name)}`);

      if (!userRequired) {
        next();
        return;
      }

      if (!auth.isAuthenticated()) {
        Log.tag('user').green('Routing').info('No user -> Redirecting to login');

        next({
          name: 'account.login',
          query: {
            redirect: encodeURIComponent(from.fullPath),
          },
        });

        return;
      }

      Log.tag('user').green('Routing').info('User', userStore.active?.email);

      next();
      return;
    });

    try {
      const user = await client.getUser();

      userStore.update(user.data);
      auth.authenticateAs('user', async () => {
        try {
          const currentProject =
            (appRouter.currentRoute.value.query.project as string) || projectStore.active?.id;

          const response = await retry(
            () =>
              client.getToken({
                ...(currentProject && { project: currentProject }),
              }),
            {
              retries: 2,
              factor: 2,
              minTimeout: 1000,
            },
          );

          const { token } = response.data;

          return token;
        } catch (error: any) {
          if (isHttpError(error) && [401, 419].includes(error.response?.status || 0)) {
            auth.unAuthenticate();
            appRouter.push({
              name: 'account.login',
              query: {
                redirect: encodeURIComponent(appRouter.currentRoute.value.fullPath),
              },
            });

            return '';
          } else if (
            isHttpError(error) &&
            error.response?.status === 403 &&
            error.response?.data?.code === '2FA_ACTION_REQUIRED'
          ) {
            window.location.href =
              '/settings/two-factor-authentication/setup-authenticator?reason=2fa_required';

            return '';
          }
          throw error;
        }
      });
    } catch (error: any) {
      if (
        isHttpError(error) &&
        error?.response?.status &&
        [401, 404, 0].includes(error.response.status)
      ) {
        userStore.update(undefined);

        if (!auth.isAuthenticatedAs('grant')) {
          auth.authenticateAs(undefined);
        }
      } else {
        throw error;
      }
    }
  },
};
